import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { Role } from '@/types/settings-api';

/**
 * Full role detail (incl. nested permission tree) for the Role edit dialog.
 *
 * GET /api/TenantRoles/{id}?TenantId={tenantId}&organizationId={orgId}
 *
 * Endpoint lives on the Identity API path prefix (`/api/TenantRoles`, Flutter
 * `IdentityService.TenantRoles`). Same base host as the rest of the app, so
 * no cross-origin work — just a different URL prefix than `/api/Role/...`
 * used by `useRolesList`.
 */
export function useRoleById(args: {
  roleId: number | null | undefined;
  tenantId: number | null | undefined;
  organizationId: string | null | undefined;
}) {
  const { roleId, tenantId, organizationId } = args;
  return useQuery({
    queryKey: ['settings', 'role-by-id', roleId, tenantId, organizationId],
    queryFn: ({ signal }) => {
      if (roleId == null || tenantId == null || !organizationId) {
        throw new Error('useRoleById missing required identifiers');
      }
      const qs = new URLSearchParams({
        TenantId: String(tenantId),
        organizationId,
      });
      return get<Role>(`/api/TenantRoles/${roleId}?${qs.toString()}`, { signal });
    },
    enabled: roleId != null && tenantId != null && !!organizationId,
  });
}
